import { Button } from '@/components/base/button'
import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
// import { Divider } from '@/components/base/divider'
import { useContext, useEffect, useState } from 'react'
import { AssetTypeQuery } from './__generated__/show_query'
import { Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Subheading } from '@/components/base/heading'
import { BanknotesIcon, CreditCardIcon } from '@heroicons/react/24/solid'
import { Accordion } from '@/components/accordion'
import { AttachmentResourceType, CustomFieldResource, DocumentSubjectType } from '@/types'
import { Table as CustomFieldValueTable } from '@/components/custom_field_value/table'
import { Carousel } from '../carousel'
import { CurrentTenantContext } from '@/core/current_tenant_context'
import { formatCurrency } from '@/helpers/formatCurrency'
import { Text } from '@/components/base/text'
import { Badge as CategoryBadge } from '@/components/category/badge'
import { Badge } from '../base/badge'
import { Table as ProductSpecificationTable } from '@/components/product_specification/table'
import { Table as AccessoriesTable } from '@/components/accessory/table'
import { Table as MultipliersTable } from '@/components/product_multiplier_type/table'
import { Table as MaintenanceTable } from '@/components/asset_type_maintenance_schedule/table'
import { Table as AssetTable } from '@/components/asset/table'
import { Table as EventTable } from '@/components/event/table'
import { Table as DocumentsTable } from '@/components/document/table'
import { EditDialog } from './edit_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Query as TableQuery } from './table_query'
import { DeleteDialog } from '../delete_dialog'
import { Collection as AttachmentsCollection } from '@/components/attachment/collection'

type AssetType = AssetTypeQuery['assetTypes']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [assetType, setAssetType] = useState<AssetType | null>(null)
  const { data, loading, error } = useQuery<AssetTypeQuery>(Query, { variables: { filters: { idEq: id } } })
  const { currentCompany } = useContext(CurrentTenantContext)

  useEffect(() => {
    if (!data || !id) { return }

    setAssetType(data.assetTypes.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }
  const onDelete = () => {
    setOpenDelete(true)
  }

  const onDeleteSuccess = () => {
    setOpenDelete(false)
    setOpen(false)
  }

  // categoryTags price base rate
  return (
    <>
      <Dialog open={open} size="7xl" onClose={setOpen}>
        <DialogTitle>
          {
            loading || !assetType
              ? <Loading />
              : <div className="flex flex-row justify-between">
                <div>
                  <span className="flex flex-row gap-4 items-center pb-6">
                    { assetType.name }
                    <div className="flex flex-wrap gap-2">
                      { assetType.categories.map(c => <CategoryBadge key={c.id} category={c} />) }
                      <Badge color={assetType.availableToEvents ? 'green' : 'red'}>
                        { assetType.availableToEvents ? 'Available to events' : 'Unavailable to events' }
                      </Badge>
                      <Badge color={assetType.displayOnWebsite ? 'green' : 'red'}>
                        { assetType.displayOnWebsite ? 'Display on website' : 'Hide from website' }
                      </Badge>
                    </div>
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <BanknotesIcon className="h-6 w-6" />
                    <Subheading>Cost</Subheading>
                    <Text>{ formatCurrency(currentCompany?.currency || 'GBP', assetType.costCents) }</Text>
                  </span>
                  <span className="flex flex-row gap-2 items-center">
                    <CreditCardIcon className="h-6 w-6" />
                    <Subheading>Base Rate</Subheading>
                    <Text>{ formatCurrency(currentCompany?.currency || 'GBP', assetType.rateCents) }</Text>
                  </span>
                  <div className="flex flex-col-2 gap-2">
                    <span className="flex flex-row gap-2 items-center">
                      <CreditCardIcon className="h-6 w-6" />
                      <Subheading>Barcoded Count</Subheading>
                      <Text>{ assetType.assets.count }</Text>
                    </span>
                    <span className="flex flex-row gap-2 items-center">
                      <CreditCardIcon className="h-6 w-6" />
                      <Subheading>Stock Count</Subheading>
                      <Text> { assetType.stockCount }</Text>
                    </span>
                  </div>
                  { assetType.description && (
                    <span className="flex flex-row gap-2 items-center pt-6">
                      <Text>{assetType.description}</Text>
                    </span>
                  )}
                </div>
              </div>
          }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { assetType && <div className="flex flex-col gap-4">
            <Carousel images={assetType.images} />
            {/* <Text>TODO: Calendar goes here</Text>
            <Divider /> */}
            <Accordion title="Assets" defaultOpen>
              <AssetTable filters={{ assetTypeIdEq: assetType.id }} hiddenColumns={['Name']} fixedHeight />
            </Accordion>
            <Accordion title="Specifications">
              <ProductSpecificationTable filters={{ productIdEq: assetType.id }} fixedHeight />
            </Accordion>
            <Accordion title="Accessories">
              <AccessoriesTable filters={{ assetTypeIdEq: assetType.id }} fixedHeight />
            </Accordion>
            <Accordion title="Accessory To">
              <AccessoriesTable filters={{ accessoryIdEq: assetType.id }} fixedHeight />
            </Accordion>
            <Accordion title="Multipliers">
              <MultipliersTable filters={{ productIdEq: assetType.id }} fixedHeight />
            </Accordion>
            <Accordion title="Maintenance schedules">
              <MaintenanceTable filters={{ assetTypeIdEq: assetType.id }} fixedHeight />
            </Accordion>
            <Accordion title="Stats">
              <div>
                <p>Total count: { assetType.assets.count }</p>
                <p>Total cost: { formatCurrency('gbp', assetType.assets.count * (assetType.costCents || 0)) }</p>
                <p>Revenue: { formatCurrency('gbp', assetType.revenueCents) }</p>
                <p>Return on investment: { assetType.returnOnInvestment }%</p>
              </div>
            </Accordion>
            <Accordion title="Attachments">
              <AttachmentsCollection
                filters={{ nameEq: 'attachments', resourceIdEq: assetType.id, resourceTypeEq: AttachmentResourceType.AssetType }}
                pageSize={10}
                fixedHeight
              />
            </Accordion>
            <Accordion title="Documents">
              <DocumentsTable filters={{ subjectIdEq: assetType.id, subjectTypeEq: DocumentSubjectType.AssetType }} pageSize={20} fixedHeight />
            </Accordion>
            <Accordion title="Custom fields">
              <CustomFieldValueTable
                filters={{
                  resourceIdEq: assetType.id,
                  resourceTypeEq: CustomFieldResource.AssetType
                }}
                fixedHeight
              />
            </Accordion>
            <Accordion title="Event History">
              <EventTable
                filters={{
                  eventItemsItemIdEq: assetType.id,
                }}
                fixedHeight
              />
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { assetType && <Button color="amber" onClick={onEdit}>Edit</Button> }
          { assetType && <Button color="red" onClick={onDelete}>Delete</Button> }
        </DialogActions>
      </Dialog>
      { assetType && <EditDialog id={assetType.id} open={openEdit} setOpen={setOpenEdit} /> }
      { assetType && <DeleteDialog
        id={assetType.id}
        mutation={DeleteMutation}
        open={openDelete}
        refetchQueries={[TableQuery]}
        setOpen={setOpenDelete}
        onSuccess={onDeleteSuccess}
      /> }
    </>
  )
}
